import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, BarChart3, Target, FileText, TrendingUp, Share2, Megaphone, Check, X
} from 'lucide-react';

import Navbar from '../Common/Navbar';
import MainFooter from '../Common/MainFooter';

import './homepage.component.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import { 
  getAllCategory, 
  getAllProducts, 
  getProductFeatures,
  getProductById,
  getFeatureByProduct 
} from '../../service/product.service';

// ============= DEMO MODAL COMPONENT =============
const DemoRequestModal = ({ isOpen, onClose, productName }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    userType: '',
    mobileNumber: '',
    email: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.userType) {
      newErrors.userType = 'Please select a user type';
    }

    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = 'Mobile number is required';
    } else if (!/^\+?[\d\s-]{10,}$/.test(formData.mobileNumber.trim())) {
      newErrors.mobileNumber = 'Please enter a valid mobile number';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    const payload = {
      ...formData,
      productName: productName,
      access_key: 'ffe9b71b-4e54-41ed-bb2a-c52a8b9d9ce4'
    };

    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      }).then((r) => r.json());

      if (res.success) {
        console.log('Demo request submitted successfully:', {
          ...formData,
          productName
        });

        setSubmitSuccess(true);

        setTimeout(() => {
          setFormData({
            firstName: '',
            userType: '',
            mobileNumber: '',
            email: '',
            message: ''
          });
          setSubmitSuccess(false);
          onClose();
        }, 2000);
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Error submitting demo request:', error);
      setErrors({ submit: '❌ Something went wrong. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        firstName: '',
        userType: '',
        mobileNumber: '',
        email: '',
        message: ''
      });
      setErrors({});
      setSubmitSuccess(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="demo-modal-overlay" onClick={handleClose}>
      <div className="demo-modal-container" onClick={e => e.stopPropagation()}>
        <button
          className="demo-modal-close"
          onClick={handleClose}
          disabled={isSubmitting}
          aria-label="Close modal"
        >
          <X size={24} />
        </button>

        <div className="demo-modal-header">
          <h2>Request Free Demo</h2>
          <p>Fill in your details and we'll get back to you shortly</p>
        </div>

        {submitSuccess ? (
          <div className="demo-success-message">
            <div className="success-icon">
              <Check size={48} />
            </div>
            <h3>Request Submitted Successfully!</h3>
            <p>✅ Thank you for requesting a demo! We will get back to you soon.</p>
          </div>
        ) : (
          <form className="demo-modal-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="firstName">
                First Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className={errors.firstName ? 'error' : ''}
                placeholder="Enter your first name"
                disabled={isSubmitting}
              />
              {errors.firstName && (
                <span className="error-message">{errors.firstName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="userType">
                User Type <span className="required">*</span>
              </label>
              <select
                id="userType"
                name="userType"
                value={formData.userType}
                onChange={handleChange}
                className={errors.userType ? 'error' : ''}
                disabled={isSubmitting}
              >
                <option value="">Select Type</option>
                <option value="Individual">Individual</option>
                <option value="Organisation">Organisation</option>
              </select>
              {errors.userType && (
                <span className="error-message">{errors.userType}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="mobileNumber">
                Mobile Number <span className="required">*</span>
              </label>
              <input
                type="tel"
                id="mobileNumber"
                name="mobileNumber"
                value={formData.mobileNumber}
                onChange={handleChange}
                className={errors.mobileNumber ? 'error' : ''}
                placeholder="Enter your mobile number"
                disabled={isSubmitting}
              />
              {errors.mobileNumber && (
                <span className="error-message">{errors.mobileNumber}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="email">
                Email ID <span className="required">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={errors.email ? 'error' : ''}
                placeholder="Enter your email address"
                disabled={isSubmitting}
              />
              {errors.email && (
                <span className="error-message">{errors.email}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="message">Message</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell us about your requirements (optional)"
                rows="4"
                disabled={isSubmitting}
              />
            </div>

            {errors.submit && (
              <div className="submit-error">{errors.submit}</div>
            )}

            <button
              type="submit"
              className="demo-submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Request Demo'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

// ============= HOMEPAGE COMPONENT =============

const HomePageComponent = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [activeCategory, setActiveCategory] = useState(null);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);

  const [categories, setCategories] = useState([]);
  const [productList, setProductList] = useState([]);
  const [featuresMap, setFeaturesMap] = useState({});

  const [websiteUrl, setWebsiteUrl] = useState('');
  const [error, setError] = useState(null);

  // Demo Modal States
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isDemoOpen, setIsDemoOpen] = useState(false);

  // Enterprise Section States - DYNAMIC
  const [enterpriseSections, setEnterpriseSections] = useState([]);
  const [enterpriseLoading, setEnterpriseLoading] = useState(true);
  const [enterpriseError, setEnterpriseError] = useState(null);

  // Enterprise Carousel Ref and State
  const carouselRef = useRef(null);
  const autoScrollIntervalRef = useRef(null);
  const [isCarouselHovered, setIsCarouselHovered] = useState(false);

  // Define which products should appear in enterprise carousel
  const enterpriseProductIds = [3, 7, 18,];

  const prevTestimonial = () => {
    setCurrentTestimonial((prevIndex) =>
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };

  const nextTestimonial = () => {
    setCurrentTestimonial((prevIndex) =>
      prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
    );
  };

  // Handle Try for Free button click
  const handleTryForFree = (productName) => {
    setSelectedProduct(productName);
    setIsDemoOpen(true);
  };

  // Scroll from other pages
  useEffect(() => {
    if (location.state?.scrollToProducts) {
      setTimeout(() => {
        const productsSection = document.getElementById('products');
        if (productsSection) productsSection.scrollIntoView({ behavior: 'smooth' });
      }, 120);

      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Fetch all dynamic data
  useEffect(() => {
    fetchInitialData();
    fetchEnterpriseData();
  }, []);

  // Fetch products when category changes
  useEffect(() => {
    if (activeCategory && categories.length > 0) {
      fetchProductData(activeCategory);
    }
  }, [activeCategory]);

  // Enterprise Carousel Auto-Scroll Effect
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel || enterpriseLoading) return;

    const scrollOnePage = () => {
      if (!isCarouselHovered && carousel) {
        const slideWidth = carousel.querySelector('.enterprise-slide')?.offsetWidth + 20;
        if (!slideWidth) return;
        
        carousel.scrollLeft += slideWidth;

        const maxScroll = carousel.scrollWidth / 2;
        if (carousel.scrollLeft >= maxScroll - 50) {
          carousel.style.scrollBehavior = 'auto';
          carousel.scrollLeft = 0;
          setTimeout(() => {
            carousel.style.scrollBehavior = 'smooth';
          }, 50);
        }
      }
    };

    autoScrollIntervalRef.current = setInterval(scrollOnePage, 3000);

    return () => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
      }
    };
  }, [isCarouselHovered, enterpriseLoading]);

  // Fetch Enterprise Products Data
  const fetchEnterpriseData = async () => {
    setEnterpriseLoading(true);
    setEnterpriseError(null);

    try {
      const enterpriseData = await Promise.all(
        enterpriseProductIds.map(async (productId) => {
          try {
            const product = await getProductById(productId);
            
            if (!product) {
              console.warn(`Product ${productId} not found`);
              return null;
            }

            let features = [];
            
            // Try to get features from productHighlights first
            if (product.productHighlights) {
              features = product.productHighlights
                .split(/\r?\n/)
                .map(f => f.trim())
                .filter(f => f.length > 0);
            }
            
            // Fallback to API features if highlights are not available or empty
            if (features.length === 0) {
              try {
                const apiFeatures = await getFeatureByProduct(productId);
                if (apiFeatures && Array.isArray(apiFeatures)) {
                  features = apiFeatures
                    .map(f => f.featureName || f.feature_name)
                    .filter(f => f && f.trim().length > 0);
                }
              } catch (err) {
                console.warn(`Could not fetch features for product ${productId}:`, err);
              }
            }

            // Final fallback to productOverview
            if (features.length === 0 && product.productOverview) {
              features = product.productOverview
                .split(/\r?\n/)
                .map(f => f.trim())
                .filter(f => f.length > 0)
                .slice(0, 3);
            }

            // Limit to 3 features
            features = features.slice(0, 3);

            return {
              productId: product.productId,
              title: product.productName,
              features: features,
              description: product.productDescription,
              overview: product.productOverview,
              raw: product
            };
          } catch (err) {
            console.error(`Error fetching product ${productId}:`, err);
            return null;
          }
        })
      );

      // Filter out null results
      const validData = enterpriseData.filter(item => item !== null);

      if (validData.length === 0) {
        setEnterpriseError('No enterprise products available');
      } else {
        setEnterpriseSections(validData);
        console.log('Enterprise sections loaded:', validData);
      }
    } catch (error) {
      console.error('Error fetching enterprise data:', error);
      setEnterpriseError('Failed to load enterprise products');
    } finally {
      setEnterpriseLoading(false);
    }
  };

  const fetchInitialData = async () => {
    try {
      const categoryResponse = await getAllCategory();
      console.log('Categories fetched:', categoryResponse);

      setCategories(categoryResponse);

      if (categoryResponse && categoryResponse.length > 0) {
        const firstCategoryId = categoryResponse[0].categoryId;
        setActiveCategory(firstCategoryId);

        const productResponse = await getAllProducts(firstCategoryId);
        console.log('Initial products fetched:', productResponse);
        setProductList(productResponse || []);
      }

      const featureResponse = await getProductFeatures();
      console.log('Features fetched:', featureResponse);

      const fMap = {};
      if (featureResponse && Array.isArray(featureResponse)) {
        featureResponse.forEach(f => {
          if (f.productId && f.featureName) {
            if (!fMap[f.productId]) {
              fMap[f.productId] = [];
            }
            fMap[f.productId].push(f.featureName);
          }
        });
      }
      setFeaturesMap(fMap);
      console.log('Features map created:', fMap);

    } catch (error) {
      console.error('Error fetching initial data:', error);
    }
  };

  const fetchProductData = async (categoryId) => {
    try {
      if (categoryId) {
        const productResponse = await getAllProducts(categoryId);
        console.log('Products fetched for category', categoryId, ':', productResponse);
        setProductList(productResponse || []);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      setProductList([]);
    }
  };

  const handleGetInsights = () => {
    if (!websiteUrl.trim()) {
      setError('Please enter a valid website URL');
      return;
    }

    let formatted = websiteUrl.trim();
    if (!formatted.startsWith('http')) {
      formatted = 'https://' + formatted;
    }

    setError(null);
    navigate(`/seo-results?url=${encodeURIComponent(formatted)}`);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleGetInsights();
  };

  const testimonials = [
    {
      text: "If you ask an agency to deliver 10x organic search growth...",
      author: "Fabrizio Ballarini",
      role: "Responsible for SEO, Wise",
      photo: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=120"
    },
    {
      text: "ElexytSEO allowed us to consolidate research, content, and reporting...",
      author: "Leah Martinez",
      role: "Head of Growth, Craftly",
      photo: "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=120&h=120&q=80&crop=faces"
    },
    {
      text: "Their AI suggestions directly improved rankings...",
      author: "Daniel Cho",
      role: "SEO Lead, OmniCommerce",
      photo: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=120&h=120&fit=crop&crop=face&auto=format"
    },
    {
      text: "From ideation to optimization, workflow is seamless...",
      author: "Priya Sharma",
      role: "Content Director, FinPeak",
      photo: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=120&h=120&fit=crop&face&auto=format"
    },
    {
      text: "We finally have clarity on what to write...",
      author: "Marcus Tober",
      role: "VP Marketing, Northwave",
      photo: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=120&h=120&fit=crop&face&auto=format"
    }
  ];

  useEffect(() => {
    if (!isAutoScrolling) return;

    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoScrolling, testimonials.length]);

  const nextCategory = () => {
    const currentIndex = categories.findIndex(c => c.categoryId === activeCategory);
    const nextIndex = (currentIndex + 1) % categories.length;
    setActiveCategory(categories[nextIndex].categoryId);
  };

  const prevCategory = () => {
    const currentIndex = categories.findIndex(c => c.categoryId === activeCategory);
    const prevIndex = currentIndex === 0 ? categories.length - 1 : currentIndex - 1;
    setActiveCategory(categories[prevIndex].categoryId);
  };

  const scrollCarousel = (direction) => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    const slideWidth = carousel.querySelector('.enterprise-slide')?.offsetWidth + 20;
    if (!slideWidth) return;
    
    const scrollAmount = direction === 'left' ? -slideWidth : slideWidth;

    carousel.scrollBy({
      left: scrollAmount,
      behavior: 'smooth'
    });

    if (autoScrollIntervalRef.current) {
      clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = setInterval(() => {
        if (!isCarouselHovered && carousel) {
          const slideWidth = carousel.offsetWidth;
          carousel.scrollLeft += slideWidth;

          const maxScroll = carousel.scrollWidth / 2;
          if (carousel.scrollLeft >= maxScroll) {
            carousel.style.scrollBehavior = 'auto';
            carousel.scrollLeft = 0;
            setTimeout(() => {
              carousel.style.scrollBehavior = 'smooth';
            }, 30);
          }
        }
      }, 2000);
    }
  };

  return (
    <div className="homepage bg-white">
      <Navbar />

      {/* HERO SECTION */}
      <section className="hero-section">
        <div className="container px-0 ">
          <h1 className="hero-title">
            Unleasing digital <span className='cs-span'>Intelligence to </span><br />
            transform marketing and growth
          </h1>

          <div className="search-container d-flex align-items-center">
            <input
              type="text"
              className="search-input flex-grow-1"
              placeholder="Enter website or URL"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            <button className="btn btn-insights" onClick={handleGetInsights}>
              Get insights
            </button>
          </div>

          {error && (
            <div className="alert alert-danger mt-4 mx-auto" style={{ maxWidth: "600px" }}>
              <strong>Error:</strong> {error}
            </div>
          )}

          <h2 id="products" className="toolkit-subtitle">Products For Every Need</h2>

          <div className="toolkit-tabs">
            {categories.map((cat) => (
              <button
                key={cat.categoryId}
                className={`toolkit-tab ${activeCategory === cat.categoryId ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat.categoryId)}
              >
                {cat.categoryName}
              </button>
            ))}
          </div>

          <div className="products-section">
            <div className="products-grid">
              {productList.length > 0 ? (
                productList.map((prod) => (
                  <div key={prod.productId} className="product-card">
                    <div className="product-header">
                      <h4 className="product-name">{prod.productName}</h4>
                      <p className="product-description">{prod.productDescription}</p>
                    </div>

                    {featuresMap[prod.productId] && featuresMap[prod.productId].length > 0 && (
                      <ul className="product-features" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {featuresMap[prod.productId].slice(0, 3).map((feature, idx) => (
                          <li key={idx} className="product-feature" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <Check size={16} className="feature-check-icon" style={{ flexShrink: 0 }} />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    <div className="product-actions">
                      <Link to={`/product/${prod.productId}`} className="btn btn-outline-primary">
                        Learn more
                      </Link>
                      <button
                        className="btn btn-primary"
                        onClick={() => handleTryForFree(prod.productName)}
                      >
                        Try for free
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-12 text-center py-5">
                  <p className="text-muted">No products available for this category.</p>
                </div>
              )}
            </div>

            <div className="category-navigation">
              <button className="nav-arrow prev" onClick={prevCategory}>
                <ChevronLeft size={20} />
              </button>
              <button className="nav-arrow next" onClick={nextCategory}>
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ENTERPRISE INFINITE CAROUSEL SECTION */}
<section className="enterprise-carousel-section">
  <div className="enterprise-carousel-wrapper">
    {enterpriseLoading ? (
      <div className="enterprise-loading" style={{ 
        textAlign: 'center', 
        padding: '100px 0',
        fontSize: '18px',
        color: '#666'
      }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p style={{ marginTop: '20px' }}>Loading enterprise products...</p>
      </div>
    ) : enterpriseError ? (
      <div className="enterprise-error" style={{ 
        textAlign: 'center', 
        padding: '100px 0',
        fontSize: '18px',
        color: '#dc3545'
      }}>
        <p>{enterpriseError}</p>
        <button 
          className="btn btn-primary mt-3"
          onClick={fetchEnterpriseData}
        >
          Retry
        </button>
      </div>
    ) : enterpriseSections.length > 0 ? (
      <>
        <button
          className="carousel-arrow carousel-arrow-left"
          onClick={() => scrollCarousel('left')}
          onMouseEnter={() => setIsCarouselHovered(true)}
          onMouseLeave={() => setIsCarouselHovered(false)}
        >
          <ChevronLeft size={24} />
        </button>

        <div
          ref={carouselRef}
          className="enterprise-carousel-track"
          onMouseEnter={() => setIsCarouselHovered(true)}
          onMouseLeave={() => setIsCarouselHovered(false)}
        >
          {/* Original slides */}
          {enterpriseSections.map((section, index) => (
            <div key={`original-${index}`} className="enterprise-slide">
              <div className="enterprise-slide-content">
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '30px' }}>
                    <span style={{ fontSize: '32px' }}>🚀</span>
                    <span style={{ fontSize: '16px', color: '#a0aec0' }}>
                      Elexyt Enterprise
                    </span>
                  </div>
                  <h2 className="enterprise-slide-title">{section.title}</h2>

                  <ul className="enterprise-feature-list">
                    {section.features.map((feature, idx) => (
                      <li key={idx} className="enterprise-feature-item">
                        <Check size={24} className="enterprise-check-icon" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="enterprise-product-icon-box">
                    <span>📊</span>
                  </div>
                </div>

                <Link to={`/product/${section.productId}`} className="enterprise-discover-btn">
                  Discover More
                  <ChevronRight size={16} />
                </Link>
              </div>

              <div className="enterprise-analytics-card">
                <div className="analytics-overview-box">
                  <h3 className="analytics-overview-title">Overview</h3>
                  <div className="analytics-stats-grid">
                    <div className="analytics-stat-item">
                      <h4>Active Users</h4>
                      <p>2.4M</p>
                    </div>
                    <div className="analytics-stat-item">
                      <h4>Sessions</h4>
                      <p>5.2M</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Duplicate slides for infinite scrolling */}
          {enterpriseSections.map((section, index) => (
            <div key={`duplicate-${index}`} className="enterprise-slide">
              <div className="enterprise-slide-content">
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '30px' }}>
                    <span style={{ fontSize: '32px' }}>🚀</span>
                    <span style={{ fontSize: '16px', color: '#a0aec0' }}>
                      Elexyt Enterprise
                    </span>
                  </div>
                  <h2 className="enterprise-slide-title">{section.title}</h2>

                  <ul className="enterprise-feature-list">
                    {section.features.map((feature, idx) => (
                      <li key={idx} className="enterprise-feature-item">
                        <Check size={24} className="enterprise-check-icon" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="enterprise-product-icon-box">
                    <span>📊</span>
                  </div>
                </div>

                <Link to={`/product/${section.productId}`} className="enterprise-discover-btn">
                  Discover More
                  <ChevronRight size={16} />
                </Link>
              </div>

              <div className="enterprise-analytics-card">
                <div className="analytics-overview-box">
                  <h3 className="analytics-overview-title">Overview</h3>
                  <div className="analytics-stats-grid">
                    <div className="analytics-stat-item">
                      <h4>Active Users</h4>
                      <p>2.4M</p>
                    </div>
                    <div className="analytics-stat-item">
                      <h4>Sessions</h4>
                      <p>5.2M</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <button
          className="carousel-arrow carousel-arrow-right"
          onClick={() => scrollCarousel('right')}
          onMouseEnter={() => setIsCarouselHovered(true)}
          onMouseLeave={() => setIsCarouselHovered(false)}
        >
          <ChevronRight size={24} />
        </button>
      </>
    ) : null}
  </div>
</section>

{/* Testimonial Section */}
      <section className="testimonial-section py-5">
        <div className="container">
          <h2 className="text-center fw-bold mb-5">What our clients say</h2>
          <div className="testimonial-carousel-container">
            <div className="testimonial-navigation">
              <button className="nav-arrow" onClick={prevTestimonial}>
                <ChevronLeft size={20} />
              </button>
              <div className="testimonial-slideshow">
                <div
                  className="testimonial-slides cs-slides"
                  style={{
                    transform: `translateX(-${currentTestimonial * 100}%)`,
                    transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  {testimonials.map((testimonial, index) => (
                    <div key={index} className="testimonial-slide">
                      <div className="testimonial-card">
                        <div className="testimonial-content">
                          <div className="testimonial-text">
                            "{testimonial.text}"
                          </div>
                          <div className="testimonial-author-section">
                            <img
                              src={testimonial.photo || "/api/placeholder/120/120"}
                              alt={testimonial.author}
                              className="author-avatar"
                            />
                            <div className="author-info">
                              <h5>{testimonial.author}</h5>
                              <p>{testimonial.role}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <button className="nav-arrow" onClick={nextTestimonial}>
                <ChevronRight size={20} />
              </button>
            </div>
            <div className="testimonial-indicators">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  className={`indicator ${index === currentTestimonial ? 'active' : ''}`}
                  onClick={() => {
                    setCurrentTestimonial(index);
                    setIsAutoScrolling(false);
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <DemoRequestModal
        isOpen={isDemoOpen}
        onClose={() => setIsDemoOpen(false)}
        productName={selectedProduct}
      />

      <MainFooter />
    </div>
  );
};

export default HomePageComponent;